import React, { useMemo } from 'react';
import { ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { ApolloProvider } from '@apollo/client';
import { getDeployedEnvType, useNextMiApolloClient } from '@marriott/mi-apollo-client-utils';
import { Layout, respGridUtil, prePageCall } from '@marriott/mi-headless-utils';
import { backgroundColorMapping, constants, IMAGE_URL_DOMAIN, PAGES } from '@marriott/mi-rnb-components/constants';
import { GetServerSideProps } from 'next';
import { PageParamsContext } from '@marriott/mi-rnb-components';
import operationSignatures from '../../_constants/operationSignatures.json';

const { NEXT_PUBLIC_AEM_PATH } = process.env;
const { ENV_DEV } = process.env;
const isDev = process.env['NODE_ENV'] === ENV_DEV;

const mockModel = async () => {
  if (isDev) {
    // nx-ignore-next-line
    const { golfOverviewModel } = await import('@marriott/mi-rnb-components/localModel');
    return golfOverviewModel;
  }
  return {};
};

const { ITEM_PATH_ROOT, ITEM_PATH_FOOTER, ITEM_PATH_HEADER } = constants;
const { GOLF_OVERVIEW_PAGE } = PAGES;

// final data would have to come from 2 calls:
// 1. Page level call from AEM - that will populate the labels on components
// 2. Functional data for components like list of amenities and other related information
// import jsonContent from '../../mock/label-info.json';

export default function HomePage({ model, deployedEnvType, isAuthorMode, cookies, currentLocale }) {
  const MiApolloClient = useNextMiApolloClient(operationSignatures, deployedEnvType);

  const responsiveGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid);
  const responsiveGridHeaderModel = respGridUtil(model?.cqItems?.root?.[':items']?.['header-responsivegrid']);
  const responsiveGridFooterModel = respGridUtil(model?.cqItems?.root?.[':items']?.['footer-responsivegrid']);

  const {
    metaNames,
    metaProperties,
    canonical,
    title,
    staticComponentClientLibsJS,
    staticComponentClientLibsCSS,
    gridCss,
    bgColor,
    hrefLangList,
  } = model;

  const contextValue = useMemo(
    () => ({
      userId: cookies.UserIdToken,
      currentPage: GOLF_OVERVIEW_PAGE,
      backgroundColor: bgColor,
      currentLocale,
    }),
    [bgColor, cookies.UserIdToken, currentLocale]
  );

  const bgColorSearch = backgroundColorMapping[bgColor];

  return (
    <ApolloProvider client={MiApolloClient}>
      <PageParamsContext.Provider value={contextValue}>
        <Layout
          metaNames={metaNames}
          metaProps={metaProperties}
          title={title}
          staticComponentClientLibsCSS={staticComponentClientLibsCSS}
          staticComponentClientLibsJS={staticComponentClientLibsJS}
          isAuthorMode={isAuthorMode}
          gridCss={gridCss}
          canonical={canonical}
          hreflangList={hrefLangList}
        >
          <section>
            {responsiveGridHeaderModel && (
              <ResponsiveGrid
                {...responsiveGridHeaderModel}
                model={responsiveGridHeaderModel}
                pagePath={NEXT_PUBLIC_AEM_PATH}
                itemPath={ITEM_PATH_HEADER}
              />
            )}

            <div style={{ background: bgColorSearch }}>
              <ResponsiveGrid
                {...responsiveGridModel}
                model={responsiveGridModel}
                pagePath={NEXT_PUBLIC_AEM_PATH}
                itemPath={ITEM_PATH_ROOT}
              />
            </div>

            {responsiveGridFooterModel && (
              <ResponsiveGrid
                {...responsiveGridFooterModel}
                model={responsiveGridFooterModel}
                pagePath={NEXT_PUBLIC_AEM_PATH}
                itemPath={ITEM_PATH_FOOTER}
              />
            )}
          </section>
        </Layout>
      </PageParamsContext.Provider>
    </ApolloProvider>
  );
}

//Any product specific page data apart from common data like session, datalayer
//can be returned from this function and the same is passed as props to the page Component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rnbSpecificData = async () => {
  //any page specific code can be written here.
  const deployedEnvType = getDeployedEnvType();

  return {
    operationSignatures: operationSignatures,
    deployedEnvType,
  };
};

//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getServerSideProps: GetServerSideProps<any> = prePageCall(rnbSpecificData, mockModel, false, true, true);
